import React, { useEffect, useMemo, useState } from 'react';
import { ChevronRight, Plus, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getSitesForCurrentUser } from '../SiteManagement/siteApi';

const STORAGE_PREFIX = 'videoAttendance.zoneDesigner.';
const DUMMY_FRAME_HOST_RE = /(^|\/\/)images\.unsplash\.com\//i;
const DUMMY_CAMERA_IDS = new Set(['default', 'cam1', 'cam2', 'cam3', 'cam4', '1']);

function resolveSiteValue(site) {
  if (!site || typeof site !== 'object') return '';
  const v = site.site_id ?? site.id ?? site.siteId ?? site.site_code;
  return v != null ? String(v).trim() : '';
}

function siteDisplayName(site) {
  if (!site || typeof site !== 'object') return 'Site';
  return site.site_name || site.name || site.display_name || 'Site';
}

function listSavedCameraIds() {
  try {
    const ids = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_PREFIX)) continue;
      const id = k.slice(STORAGE_PREFIX.length);
      ids.push(id);
    }
    return ids.sort((a, b) => a.localeCompare(b));
  } catch {
    return [];
  }
}

function getSavedSummary(cameraId) {
  try {
    const raw = localStorage.getItem(`${STORAGE_PREFIX}${cameraId}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const zones = Array.isArray(parsed?.zones) ? parsed.zones : [];
    const rawFrameUrl = typeof parsed?.frameUrl === 'string' ? parsed.frameUrl : '';
    const frameUrl = DUMMY_FRAME_HOST_RE.test(rawFrameUrl) ? '' : rawFrameUrl;
    const site_id = typeof parsed?.site_id === 'string' ? parsed.site_id : '';
    return { zonesCount: zones.length, frameUrl, site_id };
  } catch {
    return null;
  }
}

function removeSaved(cameraId) {
  localStorage.removeItem(`${STORAGE_PREFIX}${cameraId}`);
}

function sanitizeSavedConfig(cameraId) {
  try {
    const key = `${STORAGE_PREFIX}${cameraId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return false;
    const parsed = JSON.parse(raw);
    const rawFrameUrl = typeof parsed?.frameUrl === 'string' ? parsed.frameUrl : '';
    const hasDummyFrame = DUMMY_FRAME_HOST_RE.test(rawFrameUrl);
    if (!hasDummyFrame) return false;

    // If it looks like an old demo entry, delete it outright.
    if (DUMMY_CAMERA_IDS.has(String(cameraId || '').trim().toLowerCase())) {
      localStorage.removeItem(key);
      return true;
    }

    // Otherwise, keep the config but strip dummy frameUrl.
    localStorage.setItem(
      key,
      JSON.stringify({
        ...parsed,
        frameUrl: '',
      })
    );
    return true;
  } catch {
    return false;
  }
}

const ZonesHomeScreen = () => {
  const navigate = useNavigate();
  const [savedIds, setSavedIds] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');

  const refreshSaved = () => setSavedIds(listSavedCameraIds());

  useEffect(() => {
    refreshSaved();
  }, []);

  // Cleanup old demo/local dummy data (e.g., Unsplash frame URLs) so the list
  // doesn't show hardcoded placeholder content.
  useEffect(() => {
    if (!savedIds.length) return;
    let changed = false;
    for (const id of savedIds) {
      changed = sanitizeSavedConfig(id) || changed;
    }
    if (changed) refreshSaved();
  }, [savedIds.length]);

  useEffect(() => {
    let cancelled = false;
    setSitesLoading(true);
    setSitesError('');
    (async () => {
      try {
        const response = await getSitesForCurrentUser();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
        const sorted = [...list].sort((a, b) => siteDisplayName(a).localeCompare(siteDisplayName(b)));
        if (!cancelled) setSites(sorted);
      } catch (e) {
        if (!cancelled) {
          setSites([]);
          setSitesError(e?.message || 'Unable to load sites.');
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const summaries = useMemo(() => {
    const map = new Map();
    for (const id of savedIds) {
      map.set(id, getSavedSummary(id));
    }
    return map;
  }, [savedIds]);

  const localSiteIds = useMemo(() => {
    const set = new Set();
    for (const id of savedIds) {
      const s = summaries.get(id);
      const sid = String(s?.site_id || '').trim();
      if (sid) set.add(sid);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [savedIds, summaries]);

  const filteredIds = useMemo(() => {
    const list = Array.isArray(savedIds) ? savedIds : [];
    if (!siteId) return list;
    return list.filter(id => {
      const s = summaries.get(id);
      return String(s?.site_id || '').trim() === String(siteId).trim();
    });
  }, [savedIds, siteId, summaries]);

  const openDesigner = nextId => {
    const id = String(nextId || '').trim();
    const qs = new URLSearchParams();
    if (id) qs.set('camera_id', id);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    navigate(`/video-attendance/zones${suffix}`);
  };

  return (
    <div className="w-full min-h-full flex flex-col gap-4 rounded-xl">
      <section className="px-2 sm:px-3 py-2">
        <div className="flex items-center justify-between gap-3">
          <h1 className="text-2xl sm:text-3xl font-semibold text-neutral-900 flex items-center gap-2">
            Zones
          </h1>
          <button
            type="button"
            onClick={() => navigate('/video-attendance/zones')}
            className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white !text-white px-4 py-2 text-sm font-semibold hover:bg-emerald-700"
          >
            <Plus className="w-4 h-4 text-white" />
            Add Zone
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-12 gap-4 flex-1 min-h-0 px-2 sm:px-3 pb-3">
        <div className="lg:col-span-12 min-h-0 flex flex-col">
          <div className="rounded-lg border border-white/60 bg-white/55 p-3 mb-3">
            <label className="block text-sm font-semibold text-neutral-900 mb-1">Site</label>
            <select
              value={siteId}
              onChange={e => setSiteId(e.target.value)}
              disabled={sitesLoading}
              className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:opacity-70"
            >
              <option value="">{sitesLoading ? 'Loading sites…' : 'All sites'}</option>
              {sites.length
                ? sites.map(site => {
                    const id = resolveSiteValue(site);
                    if (!id) return null;
                    return (
                      <option key={id} value={id}>
                        {siteDisplayName(site)}
                      </option>
                    );
                  })
                : localSiteIds.map(id => (
                    <option key={id} value={id}>
                      {id}
                    </option>
                  ))}
            </select>
            {sitesError ? (
              <p className="mt-1 text-xs text-rose-600">
                {sitesError} Showing site filter from local saves.
              </p>
            ) : null}
          </div>

          <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-white/60 bg-white/55">
            {savedIds.length === 0 ? (
              <div className="p-4">
                <p className="text-sm text-neutral-700">No saved zone configs yet.</p>
                <p className="text-xs text-neutral-600 mt-1">
                  Create zones in the designer and click Save.
                </p>
              </div>
            ) : filteredIds.length === 0 ? (
              <div className="p-4">
                <p className="text-sm text-neutral-700">No saved zone configs for this site.</p>
                <p className="text-xs text-neutral-600 mt-1">Switch site filter to see other saved configs.</p>
              </div>
            ) : (
              <div className="divide-y divide-white/70">
                {filteredIds.map(id => {
                  const summary = summaries.get(id);
                  return (
                    <div key={id} className="p-3 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-neutral-900 truncate">{id}</p>
                        <p className="text-xs text-neutral-700 mt-0.5">
                          Zones: <span className="font-semibold">{summary?.zonesCount ?? 0}</span>
                        </p>
                        {summary?.site_id ? (
                          <p className="text-[11px] text-neutral-600 mt-1 truncate">
                            Site: <span className="font-semibold">{summary.site_id}</span>
                          </p>
                        ) : null}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          type="button"
                          onClick={() => openDesigner(id)}
                          className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white !text-white px-3 py-2 text-sm font-semibold hover:bg-emerald-700"
                        >
                          Open
                          <ChevronRight className="w-4 h-4 text-white" />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            removeSaved(id);
                            refreshSaved();
                          }}
                          className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-100"
                          title="Delete saved config"
                        >
                          <Trash2 className="w-4 h-4" />
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ZonesHomeScreen;

